import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  ExtractHttpErrorResponseCodeAndMessage,
  IHttpErrorResponseCodeAndMessage,
  IHttpSingleDataResponse,
  ToastService,
} from '@irembo-andela/irembogov3-common';
import { finalize } from 'rxjs';
import { IServiceChangeRequest } from '../../../core/models/service-change-request.model';
import { ServiceChangeRequestService } from '../../../core/services/service-change-request.service';
import { IServiceConfigStep } from '../../../core/models/service-change-request-config-steps-forms.model';
import { CdkStepper, StepperSelectionEvent } from '@angular/cdk/stepper';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { DefaultEmptyFormData } from '../../../core/models/form-builder-fields-data.model';
import { FormBuilderService } from '../../../core/services/form-builder.service';
import { ServiceTemplateService } from '../../../core/services/service-template.service';
import { IServiceTemplateSummary } from '../../../core/models/service-template/service-template.model';

@Component({
  selector: 'irembogov-configure-service-change-request',
  templateUrl: './configure-service-change-request.component.html',
  providers: [{ provide: CdkStepper }],
})
export class ConfigureServiceChangeRequestComponent
  implements OnInit, OnDestroy
{
  isLoadingChangeRequest = false;
  loadRequestDetailsFailed = false;
  isLoadingDetails = false;
  loadFailedErrorCodeMessage!: IHttpErrorResponseCodeAndMessage;
  requestId!: string;

  isSubmittingForQA = false;

  showFormPreview = false;

  @ViewChild('templates', { static: true })
  templatesTemplate!: TemplateRef<any>;
  @ViewChild('overview', { static: true }) overviewTemplate!: TemplateRef<any>;
  @ViewChild('formbuilder', { static: false })
  formbuilderTemplate!: TemplateRef<any>;
  @ViewChild('pricing', { static: false }) pricingTemplate!: TemplateRef<any>;
  @ViewChild('officerform', { static: false })
  officerformTemplate!: TemplateRef<any>;
  @ViewChild('workflow', { static: false }) workflowTemplate!: TemplateRef<any>;
  @ViewChild('translation', { static: false })
  translationTemplate!: TemplateRef<any>;
  @ViewChild('servicemapping', { static: false })
  servicemappingTemplate!: TemplateRef<any>;
  @ViewChild('journeyv3config', { static: false })
  journeyv3configTemplate!: TemplateRef<any>;

  configSteps: IServiceConfigStep[] = [
    {
      label: 'Templates',
      key: 'templates',
      icon: 'fas fa-layer-group',
      isComplete: false,
      hasError: false,
    },
    {
      label: 'Overview',
      key: 'overview',
      icon: 'fas fa-clock',
      isComplete: false,
      hasError: false,
    },
    {
      label: 'Form Builder',
      key: 'formbuilder',
      icon: 'fas fa-border-none',
      isComplete: false,
      hasError: false,
    },
    {
      label: 'Pricing',
      key: 'pricing',
      icon: 'fas fa-money-bill-1-wave',
      isComplete: false,
      hasError: false,
    },
    {
      label: 'Workflow',
      key: 'workflow',
      icon: 'fas fa-diagram-project',
      isComplete: false,
      hasError: false,
    },
    {
      label: 'Officer Form',
      key: 'officerform',
      icon: 'fas fa-file',
      isComplete: false,
      hasError: false,
    },
    {
      label: 'Translation',
      key: 'translation',
      icon: 'fas fa-language',
      isComplete: false,
      hasError: false,
    },
  ];

  currentConfigStep = 0;

  // Builder-version flag is owned here so the toggle can live in the top header
  // (shown only on the Form Builder step) while the form-builder step consumes
  // it as an input.
  useV2Builder = localStorage.getItem('form-builder-version') === 'v2';

  changeRequest!: IServiceChangeRequest;

  changeRequestJsonFormData!: Record<string, FormlyFieldConfig[]>;

  changeRequestJsonWorkflowData!: Record<string, unknown>;

  changeRequestTranslationsData!: Record<string, Record<string, unknown>>;

  serviceChangeRequestFormFields!: any;

  serviceChangeRequestPricingData!: Record<string, unknown>;

  readyForSaveSubmitForReview = false;

  changeRequestThirdPartyFormData!: Record<string, unknown>;

  loadingOfficerThirdPartyForm = false;

  // Template selection properties
  selectedTemplate: IServiceTemplateSummary | null = null;
  templateSelected = false;
  isApplyingTemplate = false;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    private serviceChangeRequestService: ServiceChangeRequestService,
    private formBuilderService: FormBuilderService,
    private serviceTemplateService: ServiceTemplateService,
    public toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params: ParamMap) => {
      const paramRequestId: string | null = params.get('requestId');
      this.serviceChangeRequestService.requestId.next(paramRequestId);
      if (paramRequestId) {
        this.requestId = paramRequestId;
        this.isLoadingChangeRequest = true;
        this.getChangeRequest();
      }
    });
    this.formBuilderService.startFormBuilderConfigurationSubjects();
  }

  updateConfigSteps(): void {
    // Remove any existing service mapping or journey v3 config steps
    const serviceMappingIndex = this.configSteps.findIndex(
      step => step.key === 'servicemapping'
    );
    if (serviceMappingIndex !== -1) {
      this.configSteps.splice(serviceMappingIndex, 1);
    }

    const journeyV3Index = this.configSteps.findIndex(
      step => step.key === 'journeyv3config'
    );
    if (journeyV3Index !== -1) {
      this.configSteps.splice(journeyV3Index, 1);
    }

    // Add appropriate step based on service type
    if (
      this.changeRequest?.serviceType === 'JOURNEY' ||
      this.changeRequest?.serviceType === 'JOURNEY_V2'
    ) {
      // Add service mapping step for V2
      const formBuilderIndex = this.configSteps.findIndex(
        step => step.key === 'formbuilder'
      );

      if (formBuilderIndex !== -1) {
        const serviceMappingStep: IServiceConfigStep = {
          label: 'Service Mapping',
          key: 'servicemapping',
          icon: 'fas fa-map',
          isComplete: false,
          hasError: false,
        };

        this.configSteps.splice(formBuilderIndex, 0, serviceMappingStep);
      }
    } else if (this.changeRequest?.serviceType === 'JOURNEY_V3') {
      // Add journey v3 config step for V3 at index 6
      const journeyV3ConfigStep: IServiceConfigStep = {
        label: 'Journey Config',
        key: 'journeyv3config',
        icon: 'fas fa-cogs',
        isComplete: false,
        hasError: false,
      };

      // Insert at index 6, or at the end if there are fewer steps
      const insertIndex = Math.min(5, this.configSteps.length);
      this.configSteps.splice(insertIndex, 0, journeyV3ConfigStep);
    }
  }

  getChangeRequestPricingData() {
    this.isLoadingDetails = true;
    this.serviceChangeRequestService
      .getServiceChangeRequestPricingDetails(this.requestId)
      .pipe(finalize(() => (this.isLoadingDetails = false)))
      .subscribe({
        next: (response: IHttpSingleDataResponse<Record<string, unknown>>) => {
          this.serviceChangeRequestPricingData = response.data;
        },
        error: () => {
          this.serviceChangeRequestPricingData = {};
        },
      });
  }

  getChangeRequest() {
    this.isLoadingDetails = true;
    this.serviceChangeRequestService
      .getAssignedServiceChangeRequestById(this.requestId)
      .pipe(
        finalize(() => {
          this.isLoadingChangeRequest = false;
          this.isLoadingDetails = false;
        })
      )
      .subscribe({
        next: (response: IHttpSingleDataResponse<IServiceChangeRequest>) => {
          this.changeRequest = response.data;
          this.updateConfigSteps();
        },
        error: (error: HttpErrorResponse) => {
          this.loadRequestDetailsFailed = true;
          this.loadFailedErrorCodeMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              `Failed to load Service Change Request: #${this.requestId}`
            );
        },
      });
  }

  getChangeRequestFormDefinition() {
    this.isLoadingDetails = true;
    this.serviceChangeRequestService
      .getServiceChangeRequestFormDefinition(this.requestId)
      .pipe(
        finalize(() => {
          this.isLoadingDetails = false;
        })
      )
      .subscribe({
        next: (
          response: IHttpSingleDataResponse<Record<string, FormlyFieldConfig[]>>
        ) => {
          this.changeRequestJsonFormData = response.data;
          this.serviceChangeRequestFormFields = response.data;
          this.formBuilderService.loadJsonFormDataForPreview(
            this.changeRequestJsonFormData
          );
        },
        error: () => {
          this.changeRequestJsonFormData = DefaultEmptyFormData;
        },
      });
  }

  getChangeRequestWorkflowDefinition() {
    this.isLoadingDetails = true;
    this.serviceChangeRequestService
      .getServiceChangeRequestWorkflowDefinition(this.requestId)
      .pipe(
        finalize(() => {
          this.isLoadingDetails = false;
        })
      )
      .subscribe({
        next: (response: IHttpSingleDataResponse<Record<string, unknown>>) => {
          this.changeRequestJsonWorkflowData = response.data;
        },
        error: () => {
          this.changeRequestJsonWorkflowData = {};
        },
      });
  }

  getChangeRequestWorkflowDefinitionAndNextStepsAndProcessingTime() {
    this.isLoadingDetails = true;
    this.serviceChangeRequestService
      .getServiceChangeRequestWorkflowDefinitionAndNextStepsAndProcessingTime(
        this.requestId
      )
      .pipe(
        finalize(() => {
          this.isLoadingDetails = false;
        })
      )
      .subscribe({
        next: (response: IHttpSingleDataResponse<Record<string, unknown>>) => {
          this.changeRequestJsonWorkflowData = response.data;
        },
        error: () => {
          this.changeRequestJsonWorkflowData = {};
        },
      });
  }

  getChangeRequestThirdPartyFormDefination(options?: {
    officerStepOverlay?: boolean;
  }) {
    const overlay = options?.officerStepOverlay === true;
    if (overlay) {
      this.loadingOfficerThirdPartyForm = true;
    } else {
      this.isLoadingDetails = true;
    }
    this.serviceChangeRequestService
      .getServiceChangeRequestThirdPartyFormDefination(this.requestId)
      .pipe(
        finalize(() => {
          if (overlay) {
            this.loadingOfficerThirdPartyForm = false;
          } else {
            this.isLoadingDetails = false;
          }
        })
      )
      .subscribe({
        next: (response: IHttpSingleDataResponse<Record<string, unknown>>) => {
          this.changeRequestThirdPartyFormData = response.data;
        },
        error: () => {
          this.changeRequestThirdPartyFormData = {};
        },
      });
  }

  onOfficerThirdPartyFormPersisted(data: Record<string, unknown>): void {
    this.changeRequestThirdPartyFormData = data;
  }

  getChangeRequestTranslationsData() {
    this.isLoadingDetails = true;
    this.serviceChangeRequestService
      .getServiceChangeRequestTranslation(this.requestId)
      .pipe(finalize(() => (this.isLoadingDetails = false)))
      .subscribe({
        next: (
          response: IHttpSingleDataResponse<
            Record<string, Record<string, unknown>>
          >
        ) => {
          this.changeRequestTranslationsData = response.data;
        },
        error: () => {
          this.changeRequestTranslationsData = {};
        },
      });
  }

  getCurrentTemplate(index: number): TemplateRef<ElementRef> {
    if (index >= 0 && index < this.configSteps.length) {
      const templateKey = `${this.configSteps[index].key}Template`;
      return this[
        templateKey as keyof ConfigureServiceChangeRequestComponent
      ] as TemplateRef<ElementRef>;
    }
    return this.templatesTemplate;
  }

  onStepFormSubmitted(isValid: boolean) {
    this.configSteps[this.currentConfigStep].isComplete = isValid;
    this.handleNextStepValidity(isValid);
  }

  get isOnFormBuilderStep(): boolean {
    return this.configSteps[this.currentConfigStep]?.key === 'formbuilder';
  }

  onToggleBuilderVersion(): void {
    this.useV2Builder = !this.useV2Builder;
    localStorage.setItem(
      'form-builder-version',
      this.useV2Builder ? 'v2' : 'v1'
    );
  }

  onWorkflowStepSubmitted(data: Record<string, unknown> | undefined) {
    this.configSteps[this.currentConfigStep].isComplete = data !== undefined;
    this.handleNextStepValidity(data !== undefined);
    this.changeRequestJsonWorkflowData = data ?? {};
  }

  onServiceMappingStepSubmitted(isValid: boolean) {
    this.getChangeRequestFormDefinition();
    this.configSteps[this.currentConfigStep].isComplete = isValid;
    this.handleNextStepValidity(isValid);
  }

  onJourneyV3ConfigStepSubmitted(isValid: boolean) {
    this.configSteps[this.currentConfigStep].isComplete = isValid;
    this.handleNextStepValidity(isValid);
  }

  handleNextStepValidity(stepIsValid: boolean): void {
    if (stepIsValid) {
      this.getChangeRequest();
      this.goToNextStep();
    }
  }

  goToNextStep(): void {
    if (this.currentConfigStep < this.configSteps.length - 1) {
      if (this.currentConfigStep === 0) {
        this.getChangeRequestFormDefinition();
        this.getChangeRequestWorkflowDefinitionAndNextStepsAndProcessingTime();
        this.getChangeRequestPricingData();
        this.getChangeRequestTranslationsData();
        this.getChangeRequestThirdPartyFormDefination();
      }
      this.currentConfigStep++;
      return;
    }
    if (this.currentConfigStep >= this.configSteps.length - 1) {
      this.readyForSaveSubmitForReview = true;
    }
  }

  toggleFormPreview(event: boolean): void {
    this.showFormPreview = event;
  }

  OnStepperChange(event: StepperSelectionEvent) {
    this.currentConfigStep = event.selectedIndex;
    const currentStepKey = this.configSteps[this.currentConfigStep].key;
    if (currentStepKey === 'overview') {
      this.reloadChangeRequestValue();
    }
    if (currentStepKey === 'officerform') {
      this.getChangeRequestThirdPartyFormDefination({
        officerStepOverlay: true,
      });
    }
  }

  reloadChangeRequestValue() {
    const previousChangeRequest = this.changeRequest;
    this.changeRequest = {
      status: previousChangeRequest.status,
    } as IServiceChangeRequest;
    setTimeout(() => {
      this.changeRequest = previousChangeRequest;
    });
  }

  navigateToPage(route: string[]): void {
    this.router
      .navigate(route)
      .then()
      .catch(() => {
        return;
      });
  }

  OnSubmitToQA() {
    this.isSubmittingForQA = true;
    this.serviceChangeRequestService
      .submitChangeRequestToReviewer(this.requestId)
      .pipe(finalize(() => (this.isSubmittingForQA = false)))
      .subscribe({
        next: (res: IHttpSingleDataResponse<IServiceChangeRequest>) => {
          this.toastService.show({
            body: `${res.responseMessage}: Change request submitted for review.`,
            type: 'success',
          });
          this.navigateToPage(['/services']);
        },
        error: (error: HttpErrorResponse) => {
          const errorCodeAndMessage: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              'Failed to submit request for review.'
            );

          this.toastService.show({
            body: errorCodeAndMessage.message,
            type: 'error',
          });
        },
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  viewRequirementDocs(event: boolean) {
    return this.openFileFromServiceRequirementDocument(this.changeRequest);
  }

  openFileFromServiceRequirementDocument(changeRequest: IServiceChangeRequest) {
    if (!changeRequest.id) {
      return;
    }
    this.serviceChangeRequestService
      .openRequirementDocument(changeRequest.id)
      .subscribe({
        error: () => {
          this.toastService.show({
            body: 'Unable to download the service requirement document',
            type: 'error',
          });
        },
      });
  }

  prepareForTesting(): void {
    if (this.changeRequest.id) {
      this.isSubmittingForQA = true;
      this.serviceChangeRequestService
        .prepareForTesting(this.changeRequest.id)
        .pipe(finalize(() => (this.isSubmittingForQA = false)))
        .subscribe({
          next: response => {
            this.changeRequest = response.data;
            this.toastService.show({
              body: `Service prepared for testing successfully`,
              type: 'success',
            });
          },
          error: error => {
            const errorCodeAndMessage: IHttpErrorResponseCodeAndMessage =
              ExtractHttpErrorResponseCodeAndMessage(
                error,
                'Failed to submit request for review.'
              );

            this.toastService.show({
              body: errorCodeAndMessage.message,
              type: 'error',
            });
          },
        });
    }
  }

  // Template selection methods
  onTemplateSelected(template: IServiceTemplateSummary) {
    this.selectedTemplate = template;
    this.applyTemplateToChangeRequest(template);
  }

  onCreateWithoutTemplate() {
    this.templateSelected = true;
    this.configSteps[0].isComplete = true;
    this.goToNextStep();
  }

  private applyTemplateToChangeRequest(template: IServiceTemplateSummary) {
    this.isApplyingTemplate = true;

    this.serviceTemplateService
      .applyTemplateToChangeRequest(template.id, this.requestId)
      .pipe(finalize(() => (this.isApplyingTemplate = false)))
      .subscribe({
        next: () => {
          this.templateSelected = true;
          this.configSteps[0].isComplete = true;
          this.toastService.show({
            body: `Template "${template.name}" applied successfully`,
            type: 'success',
          });
          // Reload change request data after template application
          this.getChangeRequest();
          this.goToNextStep();
        },
        error: (error: HttpErrorResponse) => {
          const errorCodeAndMessage: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              'Failed to apply template to change request.'
            );
          this.toastService.show({
            body: errorCodeAndMessage.message,
            type: 'error',
          });
        },
      });
  }

  ngOnDestroy(): void {
    this.formBuilderService.completeFormBuilderConfigurationSubjects();
  }
}
